import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircleIcon,
  XCircleIcon,
  PhoneIcon,
  ClockIcon,
  CalendarDaysIcon,
  UserCircleIcon,
  EnvelopeIcon,
  ChatBubbleLeftIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
  BellAlertIcon,
  CheckBadgeIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import Layout from '../../components/Layout';
import api from '../../utils/api';

const COLORS = {
  navy: '#0D1B2A',
  teal: '#00B4D8',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
  grey: '#6B7280',
};

const PROGRAMME_OPTIONS = [
  { value: 'ai_basic', label: 'AI Practice Only' },
  { value: 'ai_ielts', label: 'AI IELTS Prep' },
  { value: 'ai_business', label: 'AI Business English' },
  { value: 'starter_group', label: 'Group Classes' },
  { value: 'standard', label: 'Standard' },
  { value: 'premium', label: 'Premium Group' },
  { value: 'ielts_intensive', label: 'IELTS Group Intensive' },
  { value: 'corporate', label: 'Corporate Group' },
  { value: 'online_group', label: 'Online Group Classes' },
  { value: 'online_standard', label: 'Online Standard' },
  { value: 'online_1on1', label: 'Online Premium Group' },
  { value: 'online_ielts', label: 'Online IELTS Group' },
];

const REJECT_REASONS = [
  'Not eligible',
  'No spaces available',
  'Incomplete information',
  'Wrong programme',
  'Other',
];

const inputStyle = {
  width: '100%',
  padding: '9px 13px',
  borderRadius: 8,
  border: '1.5px solid #CBD5E1',
  fontSize: 14,
  color: COLORS.navy,
  outline: 'none',
  background: '#fff',
  boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: COLORS.navy,
  marginBottom: 4,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

function Badge({ children, color }) {
  const map = {
    success: { bg: '#F0FDF4', text: '#065F46', border: '#BBF7D0' },
    warning: { bg: '#FFFBEB', text: '#92400E', border: '#FDE68A' },
    error: { bg: '#FEF2F2', text: '#991B1B', border: '#FCA5A5' },
    info: { bg: '#EFF6FF', text: '#1E40AF', border: '#BFDBFE' },
    grey: { bg: '#F1F5F9', text: '#475569', border: '#CBD5E1' },
  };
  const c = map[color] || map.grey;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 20,
      fontSize: 12, fontWeight: 700,
      background: c.bg, color: c.text, border: `1px solid ${c.border}`,
    }}>
      {children}
    </span>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 4 }}>
      {Icon && <Icon style={{ width: 14, height: 14, color: COLORS.grey, flexShrink: 0, marginTop: 2 }} />}
      <span style={{ fontSize: 12, color: COLORS.grey, fontWeight: 600, minWidth: 90 }}>{label}</span>
      <span style={{ fontSize: 13, color: COLORS.navy }}>{value}</span>
    </div>
  );
}

function Toast({ message, type, onClose }) {
  const bg = type === 'success' ? COLORS.success : type === 'error' ? COLORS.error : COLORS.navy;
  useEffect(() => {
    const t = setTimeout(onClose, 3200);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 30 }}
      style={{
        position: 'fixed', bottom: 28, right: 28, zIndex: 9999,
        background: bg, color: '#fff', padding: '12px 20px',
        borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
        fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8,
        maxWidth: 340,
      }}
    >
      {message}
      <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 0, marginLeft: 8 }}>
        <XMarkIcon style={{ width: 16, height: 16 }} />
      </button>
    </motion.div>
  );
}

function Modal({ title, onClose, children }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(13,27,42,0.6)',
        zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <motion.div
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.92, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 280, damping: 22 }}
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 16, width: '100%', maxWidth: 480,
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden',
        }}
      >
        <div style={{
          padding: '18px 24px', borderBottom: '1px solid #E2E8F0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: COLORS.navy }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <XMarkIcon style={{ width: 20, height: 20, color: COLORS.grey }} />
          </button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </motion.div>
    </motion.div>
  );
}

function ApproveModal({ registration, onClose, onSuccess }) {
  const [programme, setProgramme] = useState('standard');
  const [note, setNote] = useState(`Welcome to [school name]! We look forward to meeting you.`);
  const [appointment, setAppointment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleApprove = async () => {
    setError('');
    setLoading(true);
    try {
      await api.post(`/api/admin/registrations/${registration.id}/approve`, {
        programme,
        note_to_student: note,
        suggested_appointment: appointment || null,
      });
      onSuccess('Registration approved successfully.');
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || 'Approval failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title={`Approve — ${registration.first_name} ${registration.last_name}`} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <label style={labelStyle}>Programme</label>
          <div style={{ position: 'relative' }}>
            <select
              value={programme}
              onChange={e => setProgramme(e.target.value)}
              style={{ ...inputStyle, appearance: 'none', paddingRight: 36 }}
            >
              {PROGRAMME_OPTIONS.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
            <ChevronDownIcon style={{ width: 16, height: 16, color: COLORS.grey, position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
          </div>
        </div>

        <div>
          <label style={labelStyle}>Note to Student <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span></label>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            rows={3}
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </div>

        <div>
          <label style={labelStyle}>Suggested Appointment <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span></label>
          <input
            type="datetime-local"
            value={appointment}
            onChange={e => setAppointment(e.target.value)}
            style={inputStyle}
          />
        </div>

        {error && (
          <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 8, padding: '8px 12px', color: COLORS.error, fontSize: 13 }}>
            {error}
          </div>
        )}

        <button
          onClick={handleApprove}
          disabled={loading}
          style={{
            padding: '11px 20px', borderRadius: 10,
            background: loading ? '#94A3B8' : COLORS.success,
            color: '#fff', border: 'none', fontWeight: 700, fontSize: 15,
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
        >
          {loading ? 'Approving…' : (
            <><CheckCircleIcon style={{ width: 18, height: 18 }} /> Approve Registration</>
          )}
        </button>
      </div>
    </Modal>
  );
}

function RejectModal({ registration, onClose, onSuccess }) {
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleReject = async () => {
    if (!reason) { setError('Please select a reason.'); return; }
    setError('');
    setLoading(true);
    try {
      await api.post(`/api/admin/registrations/${registration.id}/reject`, {
        reason,
        notes: notes || null,
      });
      onSuccess('Registration rejected.');
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || 'Rejection failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title={`Reject — ${registration.first_name} ${registration.last_name}`} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <label style={labelStyle}>Reason</label>
          <div style={{ position: 'relative' }}>
            <select
              value={reason}
              onChange={e => setReason(e.target.value)}
              style={{ ...inputStyle, appearance: 'none', paddingRight: 36 }}
            >
              <option value="">Select reason…</option>
              {REJECT_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <ChevronDownIcon style={{ width: 16, height: 16, color: COLORS.grey, position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
          </div>
        </div>

        <div>
          <label style={labelStyle}>Notes <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span></label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={3}
            placeholder="Additional notes…"
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </div>

        {error && (
          <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 8, padding: '8px 12px', color: COLORS.error, fontSize: 13 }}>
            {error}
          </div>
        )}

        <button
          onClick={handleReject}
          disabled={loading}
          style={{
            padding: '11px 20px', borderRadius: 10,
            background: loading ? '#94A3B8' : COLORS.error,
            color: '#fff', border: 'none', fontWeight: 700, fontSize: 15,
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
        >
          {loading ? 'Rejecting…' : (
            <><XCircleIcon style={{ width: 18, height: 18 }} /> Reject Application</>
          )}
        </button>
      </div>
    </Modal>
  );
}

function RegistrationCard({ reg, onApprove, onReject }) {
  const [copied, setCopied] = useState(false);

  const copyPhone = () => {
    navigator.clipboard.writeText(reg.phone || '').then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const submittedAt = reg.created_at
    ? new Date(reg.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : 'Unknown';

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      layout
      style={{
        background: '#fff', borderRadius: 14, padding: 22,
        boxShadow: reg.duplicate_warnings && reg.duplicate_warnings.length > 0
          ? '0 2px 12px rgba(234,88,12,0.15)'
          : '0 2px 12px rgba(0,0,0,0.07)',
        border: reg.duplicate_warnings && reg.duplicate_warnings.length > 0
          ? '1.5px solid #FED7AA'
          : '1px solid #E2E8F0',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16, gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12, background: '#EFF6FF',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <UserCircleIcon style={{ width: 26, height: 26, color: '#3B82F6' }} />
          </div>
          <div>
            <h3 style={{ margin: '0 0 3px', fontSize: 16, fontWeight: 700, color: COLORS.navy }}>
              {reg.first_name} {reg.last_name}
            </h3>
            <p style={{ margin: 0, fontSize: 13, color: COLORS.grey }}>{reg.email}</p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <ClockIcon style={{ width: 13, height: 13, color: COLORS.grey }} />
          <span style={{ fontSize: 12, color: COLORS.grey }}>{submittedAt}</span>
        </div>
      </div>

      {/* Info grid */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap: '4px 20px',
        background: '#F8FAFC', borderRadius: 10, padding: '12px 16px', marginBottom: 14,
      }}>
        <InfoRow icon={PhoneIcon} label="Phone" value={reg.phone} />
        <InfoRow icon={CalendarDaysIcon} label="DOB" value={reg.date_of_birth} />
        <InfoRow label="Native Lang" value={reg.native_language} />
        <InfoRow label="Goal" value={reg.learning_goal} />
        <InfoRow label="Programme" value={reg.programme_interest} />
        <InfoRow label="Heard Via" value={reg.referral_source} />
      </div>

      {reg.message && (
        <div style={{
          background: '#FFFBEB', border: '1px solid #FDE68A',
          borderRadius: 8, padding: '8px 12px', marginBottom: 14,
          display: 'flex', alignItems: 'flex-start', gap: 7,
        }}>
          <ChatBubbleLeftIcon style={{ width: 14, height: 14, color: COLORS.warning, flexShrink: 0, marginTop: 2 }} />
          <p style={{ margin: 0, fontSize: 13, color: '#92400E', fontStyle: 'italic' }}>"{reg.message}"</p>
        </div>
      )}

      {/* Duplicate Warnings */}
      {reg.duplicate_warnings && reg.duplicate_warnings.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          {reg.duplicate_warnings.map((w, i) => (
            <div key={i} style={{
              background: '#FFF7ED', border: '1px solid #FED7AA',
              borderRadius: 8, padding: '9px 12px', marginBottom: 6,
              display: 'flex', alignItems: 'flex-start', gap: 8,
            }}>
              <ExclamationTriangleIcon style={{ width: 16, height: 16, color: '#EA580C', flexShrink: 0, marginTop: 1 }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#9A3412' }}>
                  {w.type === 'phone_duplicate' ? '⚠️ Duplicate phone number' : '⚠️ Possible duplicate identity'}
                </div>
                <div style={{ fontSize: 12, color: '#C2410C', marginTop: 2 }}>{w.message}</div>
                {w.match_email && (
                  <div style={{ fontSize: 11, color: '#9A3412', marginTop: 2, fontFamily: 'monospace' }}>
                    Existing: {w.match_email} ({w.match_status})
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button
          onClick={() => onApprove(reg)}
          style={{
            flex: 1, minWidth: 110, padding: '8px 14px', borderRadius: 9,
            background: COLORS.success, color: '#fff', border: 'none',
            fontWeight: 700, fontSize: 13, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
          }}
        >
          <CheckCircleIcon style={{ width: 15, height: 15 }} /> Approve
        </button>
        <button
          onClick={() => onReject(reg)}
          style={{
            flex: 1, minWidth: 110, padding: '8px 14px', borderRadius: 9,
            background: COLORS.error, color: '#fff', border: 'none',
            fontWeight: 700, fontSize: 13, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
          }}
        >
          <XCircleIcon style={{ width: 15, height: 15 }} /> Reject
        </button>
        <button
          onClick={copyPhone}
          style={{
            padding: '8px 14px', borderRadius: 9,
            background: copied ? '#F0FDF4' : '#F1F5F9',
            color: copied ? COLORS.success : COLORS.navy,
            border: `1px solid ${copied ? '#BBF7D0' : '#CBD5E1'}`,
            fontWeight: 600, fontSize: 13, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 5,
            transition: 'all 0.2s',
          }}
        >
          <PhoneIcon style={{ width: 14, height: 14 }} />
          {copied ? 'Copied!' : '📞 Call First'}
        </button>
      </div>
    </motion.div>
  );
}

function AwaitingVisitCard({ reg, onMarkVisited, onSendReminder }) {
  const approvedAt = reg.approved_at ? new Date(reg.approved_at) : null;
  const now = new Date();
  const daysSince = approvedAt
    ? Math.floor((now - approvedAt) / (1000 * 60 * 60 * 24))
    : null;

  // If auto-delete is set (e.g. 7 days), countdown
  const autoDeleteDays = 7;
  const daysLeft = daysSince !== null ? autoDeleteDays - daysSince : null;
  const urgent = daysLeft !== null && daysLeft <= 3;

  const approvedStr = approvedAt
    ? approvedAt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      layout
      style={{
        background: '#fff', borderRadius: 14, padding: 22,
        boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
        border: `1px solid ${urgent ? '#FCA5A5' : '#E2E8F0'}`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14, gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: '#F0FDF4',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <CheckBadgeIcon style={{ width: 26, height: 26, color: COLORS.success }} />
          </div>
          <div>
            <h3 style={{ margin: '0 0 3px', fontSize: 16, fontWeight: 700, color: COLORS.navy }}>
              {reg.first_name} {reg.last_name}
            </h3>
            <p style={{ margin: 0, fontSize: 13, color: COLORS.grey }}>{reg.email}</p>
          </div>
        </div>
        <Badge color="success">Approved</Badge>
      </div>

      <div style={{
        display: 'flex', gap: 20, flexWrap: 'wrap',
        background: '#F8FAFC', borderRadius: 10, padding: '10px 14px', marginBottom: 14,
      }}>
        <div>
          <p style={{ margin: '0 0 2px', fontSize: 11, fontWeight: 600, color: COLORS.grey, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Approved</p>
          <p style={{ margin: 0, fontSize: 13, color: COLORS.navy }}>{approvedStr}</p>
        </div>
        {daysSince !== null && (
          <div>
            <p style={{ margin: '0 0 2px', fontSize: 11, fontWeight: 600, color: COLORS.grey, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Days Since Approval</p>
            <p style={{ margin: 0, fontSize: 13, color: COLORS.navy }}>{daysSince} day{daysSince !== 1 ? 's' : ''}</p>
          </div>
        )}
        {daysLeft !== null && (
          <div>
            <p style={{ margin: '0 0 2px', fontSize: 11, fontWeight: 600, color: COLORS.grey, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Auto-Delete In</p>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: urgent ? COLORS.error : COLORS.navy }}>
              {urgent && <ExclamationTriangleIcon style={{ width: 14, height: 14, display: 'inline', marginRight: 3, verticalAlign: 'middle' }} />}
              {daysLeft} day{daysLeft !== 1 ? 's' : ''}
            </p>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <button
          onClick={() => onMarkVisited(reg)}
          style={{
            flex: 1, padding: '8px 14px', borderRadius: 9,
            background: COLORS.teal, color: '#fff', border: 'none',
            fontWeight: 700, fontSize: 13, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
          }}
        >
          <CheckCircleIcon style={{ width: 15, height: 15 }} /> Mark as Visited
        </button>
        <button
          onClick={() => onSendReminder(reg)}
          style={{
            padding: '8px 14px', borderRadius: 9,
            background: '#F1F5F9', color: COLORS.navy,
            border: '1px solid #CBD5E1',
            fontWeight: 600, fontSize: 13, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 5,
          }}
        >
          <BellAlertIcon style={{ width: 14, height: 14 }} /> Send Reminder
        </button>
      </div>
    </motion.div>
  );
}

function ConfirmVisitModal({ reg, onClose, onConfirm, loading }) {
  return (
    <Modal title="Mark as Visited" onClose={onClose}>
      <p style={{ margin: '0 0 20px', fontSize: 15, color: COLORS.navy }}>
        Confirm that <strong>{reg.first_name} {reg.last_name}</strong> has visited the centre and
        completed their first visit?
      </p>
      <div style={{ display: 'flex', gap: 10 }}>
        <button
          onClick={onConfirm}
          disabled={loading}
          style={{
            flex: 1, padding: '10px 16px', borderRadius: 9,
            background: loading ? '#94A3B8' : COLORS.teal,
            color: '#fff', border: 'none', fontWeight: 700, fontSize: 14,
            cursor: loading ? 'not-allowed' : 'pointer',
          }}
        >
          {loading ? 'Saving…' : 'Confirm Visit'}
        </button>
        <button
          onClick={onClose}
          style={{
            padding: '10px 16px', borderRadius: 9,
            background: '#F1F5F9', color: COLORS.navy,
            border: '1px solid #CBD5E1', fontWeight: 600, fontSize: 14, cursor: 'pointer',
          }}
        >
          Cancel
        </button>
      </div>
    </Modal>
  );
}

const Spinner = () => (
  <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ repeat: Infinity, duration: 0.9, ease: 'linear' }}
      style={{
        width: 40, height: 40, borderRadius: '50%',
        border: `4px solid ${COLORS.teal}22`, borderTopColor: COLORS.teal,
      }}
    />
  </div>
);

export default function AdminRegistrations() {
  const [activeTab, setActiveTab] = useState('pending');
  const [pending, setPending] = useState([]);
  const [awaiting, setAwaiting] = useState([]);
  const [loadingPending, setLoadingPending] = useState(true);
  const [loadingAwaiting, setLoadingAwaiting] = useState(true);
  const [approveTarget, setApproveTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [visitTarget, setVisitTarget] = useState(null);
  const [visitLoading, setVisitLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  const fetchPending = useCallback(() => {
    setLoadingPending(true);
    api.get('/api/admin/registrations')
      .then(res => setPending(res.data || []))
      .catch(() => setPending([]))
      .finally(() => setLoadingPending(false));
  }, []);

  const fetchAwaiting = useCallback(() => {
    setLoadingAwaiting(true);
    api.get('/api/admin/registrations/awaiting-visit')
      .then(res => setAwaiting(res.data || []))
      .catch(() => setAwaiting([]))
      .finally(() => setLoadingAwaiting(false));
  }, []);

  useEffect(() => { fetchPending(); }, [fetchPending]);
  useEffect(() => { fetchAwaiting(); }, [fetchAwaiting]);

  const handleApproveSuccess = (msg) => {
    showToast(msg, 'success');
    fetchPending();
    fetchAwaiting();
  };

  const handleRejectSuccess = (msg) => {
    showToast(msg, 'success');
    fetchPending();
  };

  const handleMarkVisited = async () => {
    if (!visitTarget) return;
    setVisitLoading(true);
    try {
      await api.post(`/api/admin/students/${visitTarget.id}/process-first-visit`, {});
      showToast(`${visitTarget.first_name} ${visitTarget.last_name} marked as visited.`, 'success');
      setVisitTarget(null);
      fetchAwaiting();
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to mark as visited.', 'error');
    } finally {
      setVisitLoading(false);
    }
  };

  const handleSendReminder = (reg) => {
    showToast(`Reminder sent to ${reg.first_name} ${reg.last_name}.`, 'success');
  };

  const tabStyle = (active) => ({
    padding: '10px 22px', borderRadius: 10,
    fontWeight: 700, fontSize: 14, cursor: 'pointer', border: 'none',
    background: active ? COLORS.navy : 'transparent',
    color: active ? '#fff' : COLORS.grey,
    transition: 'all 0.18s',
  });

  return (
    <Layout role="tenant_admin">
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 24px' }}>
        {/* Page header */}
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ margin: '0 0 4px', fontSize: 26, fontWeight: 800, color: COLORS.navy }}>
            Registration Inbox
          </h1>
          <p style={{ margin: 0, fontSize: 14, color: COLORS.grey }}>
            Review and process student registration requests
          </p>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'inline-flex', background: '#F1F5F9', borderRadius: 13,
          padding: 4, marginBottom: 28, gap: 2,
        }}>
          <button
            style={tabStyle(activeTab === 'pending')}
            onClick={() => setActiveTab('pending')}
          >
            Pending
            {pending.length > 0 && (
              <span style={{
                marginLeft: 7, background: COLORS.error, color: '#fff',
                borderRadius: 20, padding: '1px 7px', fontSize: 11, fontWeight: 700,
              }}>
                {pending.length}
              </span>
            )}
          </button>
          <button
            style={tabStyle(activeTab === 'awaiting')}
            onClick={() => setActiveTab('awaiting')}
          >
            Approved (Awaiting Visit)
            {awaiting.length > 0 && (
              <span style={{
                marginLeft: 7, background: COLORS.warning, color: '#fff',
                borderRadius: 20, padding: '1px 7px', fontSize: 11, fontWeight: 700,
              }}>
                {awaiting.length}
              </span>
            )}
          </button>
        </div>

        {/* Tab panels */}
        <AnimatePresence mode="wait">
          {activeTab === 'pending' && (
            <motion.div
              key="pending"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {loadingPending ? <Spinner /> : pending.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 0', color: COLORS.grey }}>
                  <CheckCircleIcon style={{ width: 52, height: 52, color: '#CBD5E1', margin: '0 auto 14px' }} />
                  <p style={{ fontSize: 16, fontWeight: 600, color: COLORS.navy, margin: '0 0 4px' }}>All clear!</p>
                  <p style={{ margin: 0, fontSize: 14 }}>No pending registrations right now.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <AnimatePresence>
                    {pending.map(reg => (
                      <RegistrationCard
                        key={reg.id}
                        reg={reg}
                        onApprove={setApproveTarget}
                        onReject={setRejectTarget}
                      />
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </motion.div>
          )}

          {activeTab === 'awaiting' && (
            <motion.div
              key="awaiting"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {loadingAwaiting ? <Spinner /> : awaiting.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 0', color: COLORS.grey }}>
                  <CalendarDaysIcon style={{ width: 52, height: 52, color: '#CBD5E1', margin: '0 auto 14px' }} />
                  <p style={{ fontSize: 16, fontWeight: 600, color: COLORS.navy, margin: '0 0 4px' }}>No students awaiting visit</p>
                  <p style={{ margin: 0, fontSize: 14 }}>Approved students will appear here until they visit the centre.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <AnimatePresence>
                    {awaiting.map(reg => (
                      <AwaitingVisitCard
                        key={reg.id}
                        reg={reg}
                        onMarkVisited={setVisitTarget}
                        onSendReminder={handleSendReminder}
                      />
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Modals */}
      <AnimatePresence>
        {approveTarget && (
          <ApproveModal
            key="approve-modal"
            registration={approveTarget}
            onClose={() => setApproveTarget(null)}
            onSuccess={handleApproveSuccess}
          />
        )}
        {rejectTarget && (
          <RejectModal
            key="reject-modal"
            registration={rejectTarget}
            onClose={() => setRejectTarget(null)}
            onSuccess={handleRejectSuccess}
          />
        )}
        {visitTarget && (
          <ConfirmVisitModal
            key="visit-modal"
            reg={visitTarget}
            onClose={() => setVisitTarget(null)}
            onConfirm={handleMarkVisited}
            loading={visitLoading}
          />
        )}
      </AnimatePresence>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <Toast
            key="toast"
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </AnimatePresence>
    </Layout>
  );
}
